import { Link } from 'react-router-dom'
import LegalLayout from '../components/LegalLayout'
import SeoHead from '../components/SeoHead'
import { LEGAL, SITE } from '../lib/links'
import { orgSchema, breadcrumbSchema } from '../lib/seo'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'

const UPDATED_AT = '3 août 2026'

export default function LegalPage() {
  const { t } = useT()
  const locale = useLocale()
  if (locale === 'en') return <LegalPageEn />
  if (locale === 'nl') return <LegalPageNl />
  return (
    <>
      <SeoHead
        title="Mentions légales — Yaslan"
        description="Mentions légales de Yaslan Logistics BV. Éditeur du site, hébergeur, propriété intellectuelle et coordonnées."
        path="/mentions-legales"
        jsonLd={[
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.legal'), path: localizedPath('legal', locale) },
          ]),
        ]}
      />
      <LegalLayout
        breadcrumb="Mentions légales"
        badge="Légal"
        title={
          <>
            Mentions{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              légales
            </span>
            .
          </>
        }
        intro={
          <>
            Informations relatives à l'éditeur du site yaslan.be conformément à la loi du 11 mars
            2003 sur les services de la société de l'information.
          </>
        }
        updatedAt={UPDATED_AT}
      >
        <h2>Éditeur du site</h2>
        <ul>
          <li><strong>{LEGAL.company}</strong> ({LEGAL.legalForm}), exploitant la marque «&nbsp;{LEGAL.brand}&nbsp;»</li>
          <li>Numéro d'entreprise (BCE)&nbsp;: {LEGAL.bce}</li>
          <li>Numéro de TVA&nbsp;: {LEGAL.vat}</li>
          <li>Siège social&nbsp;: {LEGAL.registeredOffice.city}, Belgique</li>
          <li>Entrepôt d'exploitation&nbsp;: {LEGAL.warehouse.city}, Belgique</li>
          <li>Téléphone&nbsp;: <a href={SITE.phoneHref}>{SITE.phone}</a></li>
          <li>E-mail&nbsp;: <a href={SITE.emailHref}>{SITE.email}</a></li>
          <li>Site&nbsp;: <a href={SITE.url}>{SITE.url}</a></li>
        </ul>

        <h2>Hébergeur</h2>
        <p>
          Le site yaslan.be est hébergé par <strong>Vercel Inc.</strong>, 340 S Lemon Ave #4133,
          Walnut, CA 91789, États-Unis. Site web&nbsp;: <a href="https://vercel.com" target="_blank" rel="noopener noreferrer">vercel.com</a>.
        </p>

        <h2>Directeur de la publication</h2>
        <p>
          La direction de la publication est assurée par le représentant légal de {LEGAL.company}.
        </p>

        <h2>Propriété intellectuelle</h2>
        <p>
          L'ensemble des éléments figurant sur le site yaslan.be (textes, graphismes, logos, icônes,
          images, vidéos, code source) est la propriété exclusive de {LEGAL.company} ou de ses
          partenaires, et est protégé par les législations belges et internationales relatives au
          droit d'auteur et à la propriété intellectuelle.
        </p>
        <p>
          Toute reproduction, représentation, modification, publication, adaptation totale ou partielle
          de ces éléments, quel que soit le moyen ou le procédé utilisé, est interdite sans autorisation
          écrite préalable.
        </p>
        <p>
          Les marques et logos de tiers (transporteurs, plateformes e-commerce) reproduits sur le
          site appartiennent à leurs propriétaires respectifs et ne sont utilisés qu'à titre informatif.
        </p>

        <h2>Liens hypertextes</h2>
        <p>
          Le site peut contenir des liens vers des sites tiers. {LEGAL.company} n'exerce aucun contrôle
          sur ces sites et décline toute responsabilité quant à leur contenu, leurs pratiques de
          confidentialité ou leur disponibilité.
        </p>

        <h2>Limitation de responsabilité</h2>
        <p>
          {LEGAL.company} s'efforce d'assurer l'exactitude et la mise à jour des informations
          diffusées sur le site. Toutefois, l'éditeur ne peut garantir l'absence d'erreur ou
          d'omission, et décline toute responsabilité pour les conséquences d'une utilisation des
          informations du site.
        </p>

        <h2>Données personnelles & cookies</h2>
        <p>
          Voir notre <Link to="/privacy">Politique de confidentialité</Link> et notre{' '}
          <Link to="/cookies">Politique cookies</Link>.
        </p>

        <h2>Droit applicable</h2>
        <p>
          Le présent site est soumis au droit belge. Tout litige relatif à son utilisation relève
          de la compétence exclusive du {LEGAL.jurisdiction.fr}.
        </p>
      </LegalLayout>
    </>
  )
}

function LegalPageEn() {
  const { t } = useT()
  const locale = useLocale()
  return (
    <>
      <SeoHead
        title="Legal notice — Yaslan"
        description="Legal notice of Yaslan Logistics BV. Site publisher, hosting provider, intellectual property and contact details."
        path={localizedPath('legal', locale)}
        jsonLd={[
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.legal'), path: localizedPath('legal', locale) },
          ]),
        ]}
      />
      <LegalLayout
        breadcrumb="Legal notice"
        badge="Legal"
        title={
          <>
            Legal{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              notice
            </span>
            .
          </>
        }
        intro={
          <>
            Information relating to the publisher of the website yaslan.be in accordance with the
            Belgian Act of 11 March 2003 on information society services.
          </>
        }
        updatedAt="3 August 2026"
      >
        <h2>Site publisher</h2>
        <ul>
          <li><strong>{LEGAL.company}</strong> ({LEGAL.legalForm}), operating under the brand «&nbsp;{LEGAL.brand}&nbsp;»</li>
          <li>Company number (CBE)&nbsp;: {LEGAL.bce}</li>
          <li>VAT number&nbsp;: {LEGAL.vat}</li>
          <li>Registered office&nbsp;: {LEGAL.registeredOffice.city}, Belgium</li>
          <li>Operating warehouse&nbsp;: {LEGAL.warehouse.city}, Belgium</li>
          <li>Phone&nbsp;: <a href={SITE.phoneHref}>{SITE.phone}</a></li>
          <li>E-mail&nbsp;: <a href={SITE.emailHref}>{SITE.email}</a></li>
          <li>Website&nbsp;: <a href={SITE.url}>{SITE.url}</a></li>
        </ul>

        <h2>Hosting provider</h2>
        <p>
          The website yaslan.be is hosted by <strong>Vercel Inc.</strong>, 340 S Lemon Ave #4133,
          Walnut, CA 91789, United States. Website&nbsp;: <a href="https://vercel.com" target="_blank" rel="noopener noreferrer">vercel.com</a>.
        </p>

        <h2>Publication director</h2>
        <p>
          Editorial responsibility is held by the legal representative of {LEGAL.company}.
        </p>

        <h2>Intellectual property</h2>
        <p>
          All elements appearing on the website yaslan.be (texts, graphics, logos, icons,
          images, videos, source code) are the exclusive property of {LEGAL.company} or its
          partners, and are protected by Belgian and international legislation on copyright
          and intellectual property.
        </p>
        <p>
          Any reproduction, representation, modification, publication, or partial or total adaptation
          of these elements, by any means or process, is prohibited without prior written authorisation.
        </p>
        <p>
          Third-party trade marks and logos (carriers, e-commerce platforms) reproduced on the
          website belong to their respective owners and are used for informational purposes only.
        </p>

        <h2>Hyperlinks</h2>
        <p>
          The website may contain links to third-party websites. {LEGAL.company} exercises no control
          over those websites and accepts no liability in respect of their content, privacy practices
          or availability.
        </p>

        <h2>Limitation of liability</h2>
        <p>
          {LEGAL.company} endeavours to ensure the accuracy and currency of information published
          on the website. However, the publisher cannot guarantee the absence of errors or
          omissions, and accepts no liability for the consequences of relying on the information
          contained on the website.
        </p>

        <h2>Personal data &amp; cookies</h2>
        <p>
          See our <Link to={localizedPath('privacy', locale)}>Privacy Policy</Link> and our{' '}
          <Link to={localizedPath('cookies', locale)}>Cookie Policy</Link>.
        </p>

        <h2>Governing law</h2>
        <p>
          This website is governed by Belgian law. Any dispute relating to its use falls under
          the exclusive jurisdiction of the {LEGAL.jurisdiction.en}.
        </p>
      </LegalLayout>
    </>
  )
}

function LegalPageNl() {
  const { t } = useT()
  const locale = useLocale()
  return (
    <>
      <SeoHead
        title="Juridische kennisgeving — Yaslan"
        description="Juridische kennisgeving van Yaslan Logistics BV. Uitgever van de site, hostingprovider, intellectueel eigendom en contactgegevens."
        path={localizedPath('legal', locale)}
        jsonLd={[
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.legal'), path: localizedPath('legal', locale) },
          ]),
        ]}
      />
      <LegalLayout
        breadcrumb="Juridische kennisgeving"
        badge="Juridisch"
        title={
          <>
            Juridische{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              kennisgeving
            </span>
            .
          </>
        }
        intro={
          <>
            Informatie over de uitgever van de website yaslan.be overeenkomstig de wet van 11 maart
            2003 betreffende bepaalde juridische aspecten van de diensten van de informatiemaatschappij.
          </>
        }
        updatedAt="3 augustus 2026"
      >
        <h2>Uitgever van de site</h2>
        <ul>
          <li><strong>{LEGAL.company}</strong> ({LEGAL.legalForm}), uitbater van het merk «&nbsp;{LEGAL.brand}&nbsp;»</li>
          <li>Ondernemingsnummer (KBO)&nbsp;: {LEGAL.bce}</li>
          <li>Btw-nummer&nbsp;: {LEGAL.vat}</li>
          <li>Maatschappelijke zetel&nbsp;: {LEGAL.registeredOffice.city}, België</li>
          <li>Exploitatiemagazijn&nbsp;: {LEGAL.warehouse.city}, België</li>
          <li>Telefoon&nbsp;: <a href={SITE.phoneHref}>{SITE.phone}</a></li>
          <li>E-mail&nbsp;: <a href={SITE.emailHref}>{SITE.email}</a></li>
          <li>Website&nbsp;: <a href={SITE.url}>{SITE.url}</a></li>
        </ul>

        <h2>Hostingprovider</h2>
        <p>
          De website yaslan.be wordt gehost door <strong>Vercel Inc.</strong>, 340 S Lemon Ave #4133,
          Walnut, CA 91789, Verenigde Staten. Website&nbsp;: <a href="https://vercel.com" target="_blank" rel="noopener noreferrer">vercel.com</a>.
        </p>

        <h2>Verantwoordelijke uitgever</h2>
        <p>
          De redactionele verantwoordelijkheid berust bij de wettelijke vertegenwoordiger van {LEGAL.company}.
        </p>

        <h2>Intellectueel eigendom</h2>
        <p>
          Alle elementen op de website yaslan.be (teksten, grafische elementen, logo's, pictogrammen,
          afbeeldingen, video's, broncode) zijn het exclusieve eigendom van {LEGAL.company} of haar
          partners, en worden beschermd door de Belgische en internationale wetgeving inzake
          auteursrecht en intellectueel eigendom.
        </p>
        <p>
          Elke reproductie, weergave, wijziging, publicatie, gehele of gedeeltelijke aanpassing
          van deze elementen, ongeacht het middel of de gebruikte methode, is verboden zonder
          voorafgaande schriftelijke toestemming.
        </p>
        <p>
          Handelsmerken en logo's van derden (vervoerders, e-commerceplatforms) die op de
          website zijn weergegeven, zijn eigendom van hun respectieve eigenaars en worden
          uitsluitend voor informatieve doeleinden gebruikt.
        </p>

        <h2>Hyperlinks</h2>
        <p>
          De website kan links bevatten naar websites van derden. {LEGAL.company} oefent geen
          controle uit over deze websites en wijst alle aansprakelijkheid af met betrekking tot
          hun inhoud, privacypraktijken of beschikbaarheid.
        </p>

        <h2>Aansprakelijkheidsbeperking</h2>
        <p>
          {LEGAL.company} streeft ernaar de juistheid en actualiteit van de informatie op de
          website te waarborgen. De uitgever kan echter niet garanderen dat er geen fouten of
          omissies zijn, en wijst alle aansprakelijkheid af voor de gevolgen van het gebruik
          van de informatie op de website.
        </p>

        <h2>Persoonsgegevens &amp; cookies</h2>
        <p>
          Zie ons <Link to={localizedPath('privacy', locale)}>Privacybeleid</Link> en ons{' '}
          <Link to={localizedPath('cookies', locale)}>Cookiebeleid</Link>.
        </p>

        <h2>Toepasselijk recht</h2>
        <p>
          Deze website is onderworpen aan het Belgisch recht. Elk geschil met betrekking tot het
          gebruik ervan valt onder de exclusieve bevoegdheid van de {LEGAL.jurisdiction.nl}.
        </p>
      </LegalLayout>
    </>
  )
}
